import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * Banner shown when a forecaster submits to fewer challenges per day than the
 * field expects (Rule 7 strict D+N compliance). Such forecasters get penalty
 * rows for every D+N slice they didn't fully cover and produce no
 * MarketMonthlyStats record, so the regular Analytics views render empty.
 *
 * Pair this banner with the SubmissionHeatmap so the forecaster can see which
 * specific gate closures they missed. Compliance is per-horizon, so the
 * caller is expected to pass `horizonLabel` (e.g. "D+1") to make the per-
 * horizon scope obvious.
 *
 * @param {{
 *   submitted?: number | null,
 *   total?: number | null,
 *   horizonLabel?: string | null,
 *   className?: string,
 * }} props
 */
export default function DNComplianceNotice({
  submitted,
  total,
  horizonLabel = null,
  className = '',
}) {
  const hasCounts =
    typeof submitted === 'number' && typeof total === 'number' && total > 0;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-700/60 dark:bg-amber-900/20 ${className}`.trim()}
    >
      <ExclamationTriangleIcon
        aria-hidden="true"
        className="mt-0.5 h-5 w-5 flex-none text-amber-600 dark:text-amber-400"
      />
      <div className="min-w-0 flex-1 text-sm leading-snug">
        <p className="font-semibold text-amber-800 dark:text-amber-200">
          Not evaluated this month — incomplete session coverage
          {horizonLabel ? (
            <span className="ml-2 rounded bg-amber-200/70 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-800/60 dark:text-amber-100">
              {horizonLabel}
            </span>
          ) : null}
        </p>
        <p className="mt-0.5 text-amber-700/90 dark:text-amber-300/90">
          {hasCounts ? (
            <>
              You submitted to <span className="font-mono tabular-nums font-semibold">{submitted}</span> of{' '}
              <span className="font-mono tabular-nums font-semibold">{total}</span>{' '}
              {horizonLabel ? `${horizonLabel} ` : ''}sessions this month.{' '}
            </>
          ) : null}
          Compliance is tracked per horizon: to be ranked, submit a complete
          forecast (q10, q50, q90) to{' '}
          <span className="font-semibold">every gate closure on each day</span>{' '}
          for the selected horizon. The heatmap below shows which sessions you
          covered (green) and which you missed (gray).
        </p>
      </div>
    </div>
  );
}
